import React from 'react';

export type ProvenanceState = 'measured' | 'derived' | 'self-reported' | 'missing';

const LABELS: Record<ProvenanceState, string> = {
  measured: 'Measured — a launch monitor saw it',
  derived: 'Derived — computed from things it saw',
  'self-reported': 'Self-reported — the golfer told us',
  missing: "Missing — we don't have it",
};

interface ProvenanceDotProps {
  state: ProvenanceState;
  className?: string;
}

/**
 * The product's signature (UI_DESIGN_PLAN.md §1.4): one accent (cyan, the
 * machine), one shape, confidence encoded by fill. Green never appears here —
 * it stays reserved for outcome/score so the two voices never blur.
 */
export function ProvenanceDot({ state, className = '' }: ProvenanceDotProps) {
  const base = 'inline-block w-[5px] h-[5px] rounded-full align-middle flex-none';
  const byState: Record<ProvenanceState, string> = {
    measured: 'bg-cyan',
    derived: 'bg-transparent border border-cyan',
    'self-reported': 'bg-steel',
    missing: 'bg-transparent border border-line-2',
  };

  return (
    <span
      className={`${base} ${byState[state]} ${className}`}
      role="img"
      aria-label={LABELS[state]}
      title={LABELS[state]}
    />
  );
}

const LEGEND_ORDER: ProvenanceState[] = ['measured', 'derived', 'self-reported', 'missing'];

const LEGEND_TEXT: Record<ProvenanceState, string> = {
  measured: 'measured',
  derived: 'derived',
  'self-reported': 'self-reported',
  missing: 'missing',
};

export function ProvenanceLegend({ className = '' }: { className?: string }) {
  return (
    <div className={`flex flex-wrap items-center gap-4 text-xs text-ink-3 ${className}`}>
      {LEGEND_ORDER.map((state) => (
        <span key={state} className="inline-flex items-center gap-1.5">
          <ProvenanceDot state={state} />
          {LEGEND_TEXT[state]}
        </span>
      ))}
    </div>
  );
}

export default ProvenanceDot;
